import {
  ComponentProps,
  FunctionComponent,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { Location } from "history";
import { useLocation } from "react-router-dom";

import { useLoggedInAuth } from "auth/AuthContext";
import { Button } from "components/Button/Button";
import {
  IdentitiesQuery,
  IdentitiesQueryData,
} from "components/IdentityPicker/IdentitiesQuery";
import { IdentityPicker } from "components/IdentityPicker/IdentityPicker";
import {
  IdentityPickerState,
  isPickableIdentity,
  usePickIdentity,
} from "components/IdentityPicker/utils";
import {
  PublicScreenImage,
  PublicScreenWrapperWithEnvironmentBanner,
} from "components/PublicScreenWrapper/PublicScreenWrapper";
import { Spinner } from "components/Spinner/Spinner";
import { useSetLocaleAndTimezone } from "hooks/useSetLocaleAndTimezone";
import { useTranslation } from "i18n";

// Shown after logging in to an account.
export const FullPageIdentityPicker = () => (
  <IdentitiesQuery>
    {(data) => <FullPageIdentityPickerContent {...data} />}
  </IdentitiesQuery>
);

const FullPageIdentityPickerContent = ({
  identities,
  accountLocale,
  accountTimezone,
}: IdentitiesQueryData) => {
  useSetLocaleAndTimezone(accountLocale, accountTimezone);

  const t = useTranslation();
  const { logout } = useLoggedInAuth();
  const { state }: Location<IdentityPickerState> = useLocation();

  const publicScreenImage: PublicScreenImage = "COPILOT";

  if (identities.isEmpty()) {
    return (
      <PublicScreenWrapperWithEnvironmentBanner image={publicScreenImage}>
        <div className="flex-col items-center gap-36 w-full max-w-[450px]">
          <h1 className="title">No Nabla Core API access</h1>
          <div className="text-16 leading-20 text-extension-grey-2 text-center">
            Your email address does not have access to the <b>Nabla Core API</b>
            .
          </div>
          <div className="text-14 leading-18 text-extension-grey-3 text-center">
            If you&apos;re a healthcare provider looking to use Nabla&apos;s
            app, sign in at{" "}
            <a
              className="link"
              href="https://app.nabla.com"
              target="_blank"
              rel="noreferrer"
            >
              app.nabla.com
            </a>{" "}
            instead. Otherwise, sign up using another email address or reach out
            to{" "}
            <a
              className="link"
              href="mailto:api@nabla.com"
              target="_blank"
              rel="noreferrer"
            >
              api@nabla.com
            </a>{" "}
            for assistance.
          </div>

          <Button
            secondary
            onClick={logout}
            label={t("pick_identity.logout")}
          />
        </div>
      </PublicScreenWrapperWithEnvironmentBanner>
    );
  }

  const PickerLayout = ({ children }: { children: ReactNode }) => (
    <PublicScreenWrapperWithEnvironmentBanner image={publicScreenImage}>
      <div className="flex-col items-center gap-36 w-full max-w-[450px]">
        <h1 className="title">{t("pick_identity.title")}</h1>
        {children}

        <button onClick={logout} className="p-8 text-primary hover:opacity-70">
          {t("pick_identity.logout")}
        </button>
      </div>
    </PublicScreenWrapperWithEnvironmentBanner>
  );

  return state?.shouldAutomaticallyPickSingleIdentity ? (
    <IdentityPickerWithAutomaticPickingOfSingleIdentity
      identities={identities}
      pickerLayout={PickerLayout}
    />
  ) : (
    <IdentityPickerWithLayout
      identities={identities}
      pickerLayout={PickerLayout}
    />
  );
};

// Displays an identity picker if needed, redirecting the user automatically
// if there is only a single pickable identity to choose from.
const IdentityPickerWithAutomaticPickingOfSingleIdentity = ({
  identities,
  ...rest
}: ComponentProps<typeof IdentityPickerWithLayout>) => {
  const singlePickableIdentity = useMemo(() => {
    const allIdentities = identities.flatMap(
      (it) => it.identitiesForOrganization,
    );

    const pickableIdentities = allIdentities.filter(isPickableIdentity);
    return pickableIdentities.length === 1 ? pickableIdentities[0] : null;
  }, [identities]);

  const pickIdentity = usePickIdentity();
  const shouldAutomaticallyPickSingleIdentity = singlePickableIdentity !== null;
  const startedAutomaticallyPickingSingleIdentityRef = useRef(false);

  useEffect(() => {
    if (!shouldAutomaticallyPickSingleIdentity) return;
    if (startedAutomaticallyPickingSingleIdentityRef.current) return;
    startedAutomaticallyPickingSingleIdentityRef.current = true;
    pickIdentity(singlePickableIdentity);
  }, [
    shouldAutomaticallyPickSingleIdentity,
    pickIdentity,
    singlePickableIdentity,
  ]);

  return shouldAutomaticallyPickSingleIdentity ? (
    <Spinner />
  ) : (
    <IdentityPickerWithLayout identities={identities} {...rest} />
  );
};

const IdentityPickerWithLayout = ({
  pickerLayout: PickerLayout,
  ...rest
}: ComponentProps<typeof IdentityPicker> & {
  pickerLayout: FunctionComponent<{ children: ReactNode }>;
}) => (
  <PickerLayout>
    <IdentityPicker {...rest} />
  </PickerLayout>
);
